import { useQuery } from '@tanstack/react-query';
import { Card, CardEyebrow, CardTitle, CardContent, LiveIndicator } from '@bookrcs/ui';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import type { Appointment } from '@bookrcs/types';

export default function TodayRoute() {
  const { session } = useAuth();
  const today = new Date();
  const from = startOfDay(today).toISOString();
  const to = endOfDay(today).toISOString();

  const {
    data: appointments,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['appointments', session?.proId, 'today'],
    queryFn: () =>
      api<Appointment[]>('/appointments', {
        searchParams: { proId: session?.proId ?? '', from, to },
      }),
    enabled: Boolean(session?.proId),
  });

  return (
    <div className="grid gap-6 max-w-3xl">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-[var(--text-muted)] m-0">Aujourd'hui</p>
          <h1 className="font-serif text-4xl md:text-5xl font-extrabold m-0 mt-1 text-[var(--text)]">
            {formatLongDate(today)}
          </h1>
        </div>
        <LiveIndicator label="LIVE" active />
      </header>

      <Card variant="focus">
        <CardEyebrow>Programme du jour</CardEyebrow>
        <CardTitle>Tes RDV</CardTitle>
        <CardContent>
          {isLoading ? <Skeleton lines={3} /> : null}
          {isError ? <ErrorState /> : null}
          {appointments && appointments.length === 0 ? (
            <EmptyState text="Aucun RDV aujourd'hui. Profite, ou pousse ton lien Insta." />
          ) : null}
          {appointments && appointments.length > 0 ? (
            <ul className="grid gap-2 mt-2 list-none p-0">
              {appointments.map((apt) => (
                <li
                  key={apt.id}
                  className="grid grid-cols-[72px_1fr] gap-3 items-center p-3 border border-[var(--border)] rounded-[var(--radius-card)] bg-[var(--elevated)]"
                >
                  <time className="font-mono text-sm text-[var(--text-muted)]">
                    {formatTime(apt.startAt)}
                  </time>
                  <span className="text-[var(--text)] font-bold">{apt.status}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function Skeleton({ lines }: { lines: number }) {
  return (
    <div className="grid gap-2 mt-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-12 bg-[var(--elevated)] rounded-[var(--radius-card)] animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="text-[var(--text-muted)] m-0 mt-2 italic">{text}</p>;
}

function ErrorState() {
  return (
    <p className="text-[var(--urgent)] m-0 mt-2">
      L'API ne répond pas. Vérifie que `@bookrcs/api` tourne en local (port 4000).
    </p>
  );
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function formatTime(d: Date | string): string {
  const x = typeof d === 'string' ? new Date(d) : d;
  return x.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
function formatLongDate(d: Date): string {
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}
